import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import useGeolocation, {
  detectInAppBrowser,
  geoDisponivel,
  mensagemGeoFalha,
  formatDistance,
} from '../hooks/useGeolocation';
import {
  CalendarIcon,
  ClockIcon,
  CheckCircledIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TableIcon,
  ActivityLogIcon,
  LockClosedIcon,
  InfoCircledIcon,
  SewingPinIcon,
  EnterIcon,
  ExitIcon,
  ExclamationTriangleIcon,
} from '@radix-ui/react-icons';
import {
  format,
  startOfMonth,
  endOfMonth,
  getDaysInMonth,
  getDay,
  addMonths,
  subMonths,
  isToday as isTodayFn,
  parseISO,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { minhasEscalas } from '../services/escalaApi';
import {
  DIAS_SEMANA_CURTO,
  TURNO_LABEL,
  formatDataCurta,
} from '../components/escala/escalaUtils';
import './Global.css';
import './UserDashboard.css';
import './Escala.css';

const UserDashboard = () => {
  const { user } = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);

  // Paginação padrão do histórico (mesmo hook das demais tabelas).
  const pagination = usePagination(records, 15);
  const { setPage } = pagination;
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [periodType, setPeriodType] = useState('standard');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarRecords, setCalendarRecords] = useState([]);
  const [activeTab, setActiveTab] = useState('calendar');

  // Período padrão e resumo
  const [periodSummary, setPeriodSummary] = useState(null);

  // Status de hoje
  const [todayStatus, setTodayStatus] = useState(null); // null | 'absent' | 'pending' | 'present'
  const [todayRecords, setTodayRecords] = useState([]);

  // Ponto pelo próprio militar (só efetivo do CMCB). A localização do
  // aparelho é obrigatória: sem ela o botão nem chama o servidor.
  const [punching, setPunching] = useState(false);
  const [punchStep, setPunchStep] = useState(null); // 'geo' | 'distancia' | 'envio'
  // Aviso "você está longe do colégio": { tipo, pos, distanceMeters, radiusMeters }
  const [geoAviso, setGeoAviso] = useState(null);
  const [selfPositionId, setSelfPositionId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // força recarga após bater o ponto
  const geo = useGeolocation();
  const inApp = useMemo(() => detectInAppBrowser(), []);

  // Próximos turnos na escala de itinerantes (vazio para quem não é).
  const [proximosTurnos, setProximosTurnos] = useState([]);

  // Alterar senha
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // O banco devolve "HH:MM:SS"; na tela basta HH:MM.
  const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

  const formatDecimalToHours = (decimal) => {
    if (!decimal && decimal !== 0) return '0h';
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    if (minutes === 0) return `${hours}h`;
    return `${hours}h${minutes.toString().padStart(2, '0')}min`;
  };

  const getStandardPeriodDates = useCallback(() => {
    const now = new Date();
    const day = now.getDate();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (day <= 15) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      return {
        startDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-16`,
        endDate: `${year}-${String(month + 1).padStart(2, '0')}-15`,
      };
    } else {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      return {
        startDate: `${year}-${String(month + 1).padStart(2, '0')}-16`,
        endDate: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-15`,
      };
    }
  }, []);

  // "Ciclo Anterior" = o ciclo que acabou de fechar (igual ao que o backend
  // chama de "padrão" nos relatórios). Útil em dia > 15 pra ver as horas
  // do mês de competência fechado.
  const getPreviousCyclePeriodDates = useCallback(() => {
    const now = new Date();
    const day = now.getDate();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (day <= 15) {
      // Ciclo atual ainda em curso (prev-16 → curr-15). "Anterior" = 2 meses pra trás → 1 mês pra trás.
      const startMonthIdx = (month - 2 + 12) % 12;
      const startYear = month - 2 < 0 ? year - 1 : year;
      const endMonthIdx = (month - 1 + 12) % 12;
      const endYear = month - 1 < 0 ? year - 1 : year;
      return {
        startDate: `${startYear}-${String(startMonthIdx + 1).padStart(2, '0')}-16`,
        endDate: `${endYear}-${String(endMonthIdx + 1).padStart(2, '0')}-15`,
      };
    } else {
      // Ciclo já rolou (curr-16 → next-15). "Anterior" = prev-16 → curr-15.
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      return {
        startDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-16`,
        endDate: `${year}-${String(month + 1).padStart(2, '0')}-15`,
      };
    }
  }, []);

  const getDateRange = useCallback(() => {
    const now = new Date();
    switch (periodType) {
      case 'current': {
        const start = startOfMonth(now);
        const end = endOfMonth(now);
        return {
          startDate: format(start, 'yyyy-MM-dd'),
          endDate: format(end, 'yyyy-MM-dd'),
        };
      }
      case 'previous': {
        const prev = subMonths(now, 1);
        const start = startOfMonth(prev);
        const end = endOfMonth(prev);
        return {
          startDate: format(start, 'yyyy-MM-dd'),
          endDate: format(end, 'yyyy-MM-dd'),
        };
      }
      case 'previousCycle':
        return getPreviousCyclePeriodDates();
      case 'custom':
        return { startDate: customStartDate, endDate: customEndDate };
      case 'standard':
      default:
        return getStandardPeriodDates();
    }
  }, [periodType, customStartDate, customEndDate, getStandardPeriodDates, getPreviousCyclePeriodDates]);

  const getPeriodLabel = useCallback(() => {
    const { startDate, endDate } = getDateRange();
    if (!startDate || !endDate) return '';
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      return `${format(start, 'dd/MM/yyyy')} a ${format(end, 'dd/MM/yyyy')}`;
    } catch {
      return '';
    }
  }, [getDateRange]);

  // Load user details on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        // /users/:id é só do admin — o militar lê o próprio cadastro em /users/me
        const userResponse = await api.get('/users/me');
        setUserDetails(userResponse.data);
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [user.id]);

  // Sync calendar with period selection
  useEffect(() => {
    const now = new Date();
    switch (periodType) {
      case 'current':
        setCalendarMonth(now);
        break;
      case 'previous':
        setCalendarMonth(subMonths(now, 1));
        break;
      case 'standard': {
        const day = now.getDate();
        // Show the month where most of the period falls
        if (day <= 15) {
          setCalendarMonth(subMonths(now, 1));
        } else {
          setCalendarMonth(now);
        }
        break;
      }
      case 'previousCycle': {
        // Ciclo anterior: foca no início desse ciclo
        const day = now.getDate();
        if (day <= 15) {
          setCalendarMonth(subMonths(now, 2));
        } else {
          setCalendarMonth(subMonths(now, 1));
        }
        break;
      }
      case 'custom':
        if (customStartDate) {
          try {
            setCalendarMonth(parseISO(customStartDate));
          } catch { /* ignore */ }
        }
        break;
      default:
        break;
    }
  }, [periodType, customStartDate]);

  // Load today's status (também chamado logo após bater o ponto)
  const loadTodayStatus = useCallback(async () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    try {
      const response = await api.get(`/time-records/stats/${user.id}`, {
        params: { startDate: today, endDate: today },
      });
      const recs = response.data.records || [];
      setTodayRecords(recs);
      if (recs.length === 0) {
        setTodayStatus('absent');
      } else {
        const hasPending = recs.some((r) => !r.exitTime);
        setTodayStatus(hasPending ? 'pending' : 'present');
      }
    } catch (error) {
      console.error('Erro ao carregar status de hoje:', error);
      setTodayStatus('absent');
    }
  }, [user.id]);

  // Escala de itinerantes: só aparece para quem tem turno pedido/confirmado.
  useEffect(() => {
    if (loading) return;
    minhasEscalas()
      .then((lista) => setProximosTurnos(Array.isArray(lista) ? lista : []))
      .catch(() => setProximosTurnos([]));
  }, [loading]);

  useEffect(() => {
    if (loading) return;
    loadTodayStatus();

    // Atualizar a cada 60 segundos
    const interval = setInterval(loadTodayStatus, 60000);
    return () => clearInterval(interval);
  }, [loading, loadTodayStatus]);

  /**
   * Envia o registro com a posição já capturada. Separado de baterPonto
   * porque o modal "você está longe" chama isto com a MESMA posição — não se
   * pede o GPS de novo (sem cache, o militar já esperou o fix).
   */
  const enviarPonto = async (tipo, pos) => {
    const minhasFuncoes = userDetails?.positions || [];
    setPunching(true);
    setPunchStep('envio');
    try {
      const body = {
        latitude: pos.latitude,
        longitude: pos.longitude,
        accuracy: pos.accuracy,
      };
      let response;
      if (tipo === 'entrada') {
        if (minhasFuncoes.length > 1) body.positionId = selfPositionId;
        response = await api.post('/time-records/self/entry', body);
      } else {
        response = await api.patch('/time-records/self/exit', body);
      }
      const rec = response.data || {};
      const rotulo = tipo === 'entrada' ? 'Entrada' : 'Saída';
      const hora = hhmm(tipo === 'entrada' ? rec.entryTime : rec.exitTime);
      const dist = tipo === 'entrada' ? rec.entryDistanceMeters : rec.exitDistanceMeters;
      const match = tipo === 'entrada' ? rec.entryLocationMatch : rec.exitLocationMatch;
      const precisao = pos.accuracy != null ? ` (±${Math.round(pos.accuracy)} m)` : '';
      if (dist != null && match === 'far') {
        toast(
          `${rotulo} registrada às ${hora} · a ${formatDistance(dist)} do colégio — marcada como fora do raio`,
          { icon: '⚠️', duration: 8000 },
        );
      } else if (dist != null) {
        toast.success(
          `${rotulo} registrada às ${hora} · a ${formatDistance(dist)} do colégio${precisao}`,
          { duration: 6000 },
        );
      } else {
        toast.success(`${rotulo} registrada às ${hora} · localização${precisao}`, {
          duration: 6000,
        });
      }
      setGeoAviso(null);
      await loadTodayStatus();
      setRefreshKey((k) => k + 1);
    } catch (error) {
      const msg = error.response?.data?.message;
      toast.error(
        Array.isArray(msg) ? msg.join(' ') : msg || 'Não foi possível registrar o ponto.',
        { duration: 7000 },
      );
    } finally {
      setPunching(false);
      setPunchStep(null);
    }
  };

  /**
   * Bate o ponto do próprio militar. Ordem: pede a localização ao navegador
   * e SÓ com ela segue — negou, não tem GPS ou deu timeout, o registro não
   * acontece (o servidor também recusa sem lat/long). Com a posição, confere
   * a distância ao colégio: longe → avisa ANTES de registrar (modal); o
   * servidor grava a distância de qualquer forma, o aviso é só UX.
   */
  const baterPonto = async (tipo) => {
    if (punching) return;
    const minhasFuncoes = userDetails?.positions || [];
    if (tipo === 'entrada' && minhasFuncoes.length > 1 && !selfPositionId) {
      toast.error('Selecione a função deste registro.');
      return;
    }
    setPunching(true);
    setPunchStep('geo');
    let pos;
    try {
      pos = await geo.request();
      if (pos.source !== 'gps' && pos.source !== 'network') {
        toast.error(mensagemGeoFalha(pos.source), { duration: 7000 });
        return;
      }
      setPunchStep('distancia');
      let avaliacao = null;
      try {
        const r = await api.post('/time-records/self/distance', {
          latitude: pos.latitude,
          longitude: pos.longitude,
          accuracy: pos.accuracy,
        });
        avaliacao = r.data;
      } catch {
        avaliacao = null; // pré-check indisponível: registra mesmo assim
      }
      if (avaliacao?.configurado && avaliacao.match === 'far') {
        setGeoAviso({
          tipo,
          pos,
          distanceMeters: avaliacao.distanceMeters,
          radiusMeters: avaliacao.radiusMeters,
        });
        return; // o modal decide
      }
    } finally {
      setPunching(false);
      setPunchStep(null);
    }
    await enviarPonto(tipo, pos);
  };

  const rotuloBotao = (padrao) => {
    if (!punching) return padrao;
    if (punchStep === 'distancia') return 'Conferindo distância…';
    if (punchStep === 'envio') return 'Registrando…';
    return 'Obtendo localização…';
  };

  // Load stats when period changes
  useEffect(() => {
    if (loading) return;
    const { startDate, endDate } = getDateRange();
    if (!startDate || !endDate) return;

    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const response = await api.get(`/time-records/stats/${user.id}`, {
          params: { startDate, endDate },
        });
        setStats(response.data);
        setRecords(response.data.records || []);
        setPage(1);
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
        toast.error('Erro ao carregar dados');
      } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
  }, [loading, periodType, customStartDate, customEndDate, user.id, getDateRange, setPage, refreshKey]);

  // Load calendar records for visible month
  useEffect(() => {
    if (loading) return;
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);

    const loadCalendarData = async () => {
      try {
        const response = await api.get(`/time-records/stats/${user.id}`, {
          params: {
            startDate: format(start, 'yyyy-MM-dd'),
            endDate: format(end, 'yyyy-MM-dd'),
          },
        });
        setCalendarRecords(response.data.records || []);
      } catch (error) {
        console.error('Erro ao carregar calendário:', error);
      }
    };
    loadCalendarData();
  }, [loading, calendarMonth, user.id, refreshKey]);

  // Load standard period summary
  useEffect(() => {
    if (loading) return;
    const { startDate, endDate } = getStandardPeriodDates();

    const loadPeriodSummary = async () => {
      try {
        const response = await api.get(`/time-records/stats/${user.id}`, {
          params: { startDate, endDate },
        });
        setPeriodSummary(response.data);
      } catch (error) {
        console.error('Erro ao carregar resumo do período:', error);
      }
    };
    loadPeriodSummary();
  }, [loading, user.id, getStandardPeriodDates, refreshKey]);

  // Build calendar days
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(calendarMonth);
    const firstDayOfWeek = getDay(startOfMonth(calendarMonth));
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();

    // Group records by date
    const recordsByDate = {};
    calendarRecords.forEach((record) => {
      const date = record.date;
      if (!recordsByDate[date]) {
        recordsByDate[date] = { totalHours: 0, hasExit: true, records: [] };
      }
      recordsByDate[date].totalHours += record.totalHours || 0;
      recordsByDate[date].records.push(record);
      if (!record.exitTime) {
        recordsByDate[date].hasExit = false;
      }
    });

    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ empty: true });
    }

    // Actual days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayDate = new Date(year, month, d);
      const dayData = recordsByDate[dateStr];
      let status = 'absent';
      let hours = null;

      if (dayData) {
        status = dayData.hasExit ? 'present' : 'pending';
        hours = dayData.totalHours;
      }

      const today = isTodayFn(dayDate);

      let tooltip = format(dayDate, "EEEE, dd 'de' MMMM", { locale: ptBR });
      if (dayData) {
        tooltip += ` - ${formatDecimalToHours(dayData.totalHours)}`;
        if (!dayData.hasExit) tooltip += ' (pendente)';
      }

      days.push({
        dayNumber: d,
        dateStr,
        status,
        hours,
        isToday: today,
        tooltip,
      });
    }

    return days;
  }, [calendarMonth, calendarRecords]);

  const paginatedRecords = pagination.pageItems;

  const getRankDisplay = () => {
    if (!userDetails) return '';
    const rankMap = {
      coronel: 'Cel',
      tenente_coronel: 'TC',
      major: 'Maj',
      capitao: 'Cap',
      primeiro_tenente: '1º Ten',
      segundo_tenente: '2º Ten',
      subtenente: 'ST',
      primeiro_sargento: '1º Sgt',
      segundo_sargento: '2º Sgt',
      terceiro_sargento: '3º Sgt',
      cabo: 'Cb',
      soldado: 'Sd',
    };
    // Instrutor civil não tem posto: o que aparece antes do nome é o vínculo.
    if (userDetails.personnelType === 'civil') {
      return userDetails.civilBond || '';
    }
    return rankMap[userDetails.militaryRank] || userDetails.militaryRank || '';
  };

  const getStandardPeriodLabel = () => {
    const { startDate, endDate } = getStandardPeriodDates();
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      return `${format(start, 'dd/MM/yyyy')} a ${format(end, 'dd/MM/yyyy')}`;
    } catch {
      return '';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }
    setChangingPassword(true);
    try {
      await api.patch('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      toast.success('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao alterar senha';
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      {/* Sem o Toaster montado, todo toast.* desta tela era no-op silencioso. */}
      <Toaster position="top-right" />
      <div className="container">
        {/* Welcome Header */}
        <div className="user-welcome-header">
          <div className="welcome-content">
            <p className="welcome-greeting">Bem-vindo ao seu painel</p>
            <h1 className="welcome-name">
              {[getRankDisplay(), user.name].filter(Boolean).join(' ')}
            </h1>
            <div className="welcome-meta">
              {userDetails?.matricula && (
                <span className="welcome-meta-item">
                  <InfoCircledIcon width={14} height={14} />
                  Mat. {userDetails.matricula}
                </span>
              )}
              <span className="welcome-meta-item">
                <CalendarIcon width={14} height={14} />
                {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </span>
              <button
                className="welcome-meta-item"
                onClick={() => setShowPasswordModal(true)}
                style={{ cursor: 'pointer', border: 'none' }}
              >
                <LockClosedIcon width={14} height={14} />
                Alterar Senha
              </button>
            </div>
          </div>
          <div className="welcome-icon">
            <ClockIcon width={80} height={80} />
          </div>
        </div>

        {/* Today Status Banner */}
        {todayStatus && (
          <div className={`today-status-banner today-status-${todayStatus}`}>
            <div className="today-status-icon">
              {todayStatus === 'present' && <CheckCircledIcon width={22} height={22} />}
              {todayStatus === 'pending' && <ClockIcon width={22} height={22} />}
              {todayStatus === 'absent' && <InfoCircledIcon width={22} height={22} />}
            </div>
            <div className="today-status-content">
              <span className="today-status-title">
                {todayStatus === 'present' && 'Registro completo hoje'}
                {todayStatus === 'pending' && 'Você está registrado agora'}
                {todayStatus === 'absent' && 'Sem registro hoje'}
              </span>
              <span className="today-status-detail">
                {todayStatus === 'present' &&
                  todayRecords.map((r) => `Entrada: ${hhmm(r.entryTime)} | Saída: ${hhmm(r.exitTime)} | ${formatDecimalToHours(r.totalHours)}`).join(' / ')}
                {todayStatus === 'pending' &&
                  todayRecords.filter((r) => !r.exitTime).map((r) => `Entrada registrada às ${hhmm(r.entryTime)} — aguardando saída`).join(' / ')}
                {todayStatus === 'absent' && 'Nenhuma entrada foi registrada para hoje'}
              </span>
            </div>
            {todayStatus === 'pending' && (
              <div className="today-status-live">
                <span className="live-dot"></span>
                ONLINE
              </div>
            )}
          </div>
        )}

        {/* Próximos turnos na escala de itinerantes */}
        {proximosTurnos.length > 0 && (
          <div className="esc-proximos">
            <div className="esc-proximos-cab">
              <h2>
                <CalendarIcon width={18} height={18} />
                Meus próximos turnos na escala
              </h2>
              <Link to="/escala" className="esc-proximos-link">
                Ver a escala completa <ChevronRightIcon />
              </Link>
            </div>
            <ul className="esc-proximos-lista">
              {proximosTurnos.slice(0, 8).map((t) => (
                <li key={t.id} className={`esc-proximos-item ${t.status}`}>
                  <strong>
                    {DIAS_SEMANA_CURTO[t.diaSemana]} {formatDataCurta(t.data)}
                  </strong>
                  <span>{TURNO_LABEL[t.turno]}</span>
                  <small>{t.status === 'confirmado' ? '✓ confirmado' : 'aguardando'}</small>
                </li>
              ))}
              {proximosTurnos.length > 8 && (
                <li className="esc-proximos-item">
                  <small>+ {proximosTurnos.length - 8} turno(s)</small>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Ponto pelo próprio militar — só efetivo do CMCB, sempre com localização */}
        {todayStatus && userDetails?.efetivoCmcb && (() => {
          const minhasFuncoes = userDetails.positions || [];
          const temGeo = geoDisponivel();
          const bloqueada = !temGeo || geo.permission === 'denied';
          const emAberto = todayStatus === 'pending';
          return (
            <div className="self-punch-card">
              <div className="self-punch-head">
                <div className="self-punch-icon">
                  <SewingPinIcon width={20} height={20} />
                </div>
                <div className="self-punch-text">
                  <span className="self-punch-title">Registrar meu ponto</span>
                  <span className="self-punch-detail">
                    O horário é o do sistema e a localização do seu aparelho fica gravada no
                    registro. Se você estiver longe do colégio, o sistema avisa antes de registrar.
                  </span>
                </div>
              </div>

              {!temGeo && (
                <div className="self-punch-notice danger">
                  <ExclamationTriangleIcon width={16} height={16} />
                  <span>
                    Este navegador não fornece localização. Abra o sistema no Chrome ou no Safari do celular.
                  </span>
                </div>
              )}
              {temGeo && geo.permission === 'denied' && (
                <div className="self-punch-notice danger">
                  <ExclamationTriangleIcon width={16} height={16} />
                  <span>
                    <strong>Localização bloqueada — o ponto não pode ser registrado.</strong>{' '}
                    Permita a localização para este site nas configurações do navegador (toque no
                    cadeado ao lado do endereço → Localização → Permitir) e recarregue a página.
                  </span>
                </div>
              )}
              {temGeo && geo.permission !== 'denied' && inApp.inApp && (
                <div className="self-punch-notice warn">
                  <ExclamationTriangleIcon width={16} height={16} />
                  <span>
                    Você está no navegador do {inApp.name || 'aplicativo'}. Se a localização não
                    abrir, use a opção “Abrir no navegador”.
                  </span>
                </div>
              )}

              {!emAberto && minhasFuncoes.length > 1 && (
                <label className="self-punch-field">
                  Função deste registro
                  <select
                    value={selfPositionId}
                    onChange={(e) => setSelfPositionId(e.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {minhasFuncoes.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </label>
              )}

              <div className="self-punch-actions">
                {emAberto ? (
                  <button
                    className="btn-punch exit"
                    onClick={() => baterPonto('saida')}
                    disabled={bloqueada || punching}
                  >
                    <ExitIcon width={18} height={18} />
                    {rotuloBotao('Registrar saída')}
                  </button>
                ) : (
                  <button
                    className="btn-punch entry"
                    onClick={() => baterPonto('entrada')}
                    disabled={bloqueada || punching}
                  >
                    <EnterIcon width={18} height={18} />
                    {rotuloBotao('Registrar entrada')}
                  </button>
                )}
                <span className="self-punch-foot">
                  {temGeo && geo.permission === 'granted' && 'Localização liberada.'}
                  {temGeo && geo.permission === 'prompt' &&
                    'Ao tocar, o navegador vai pedir sua localização — escolha “Permitir”.'}
                  {temGeo && (geo.permission === 'unknown' || geo.permission === 'unsupported') &&
                    'O registro exige a localização do aparelho.'}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Period Selector */}
        <div className="period-selector">
          <button
            className={`period-btn ${periodType === 'standard' ? 'active' : ''}`}
            onClick={() => setPeriodType('standard')}
          >
            Período Padrão (16-15)
          </button>
          <button
            className={`period-btn ${periodType === 'previousCycle' ? 'active' : ''}`}
            onClick={() => setPeriodType('previousCycle')}
          >
            Ciclo Anterior
          </button>
          <button
            className={`period-btn ${periodType === 'current' ? 'active' : ''}`}
            onClick={() => setPeriodType('current')}
          >
            Mês Atual
          </button>
          <button
            className={`period-btn ${periodType === 'previous' ? 'active' : ''}`}
            onClick={() => setPeriodType('previous')}
          >
            Mês Anterior
          </button>
          <button
            className={`period-btn ${periodType === 'custom' ? 'active' : ''}`}
            onClick={() => setPeriodType('custom')}
          >
            Personalizado
          </button>
          {periodType === 'custom' && (
            <div className="custom-date-inputs">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <span style={{ color: 'var(--text-muted)' }}>até</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Period Label */}
        {getPeriodLabel() && (
          <div className="period-label-wrapper">
            <span className="period-label">
              <CalendarIcon width={14} height={14} />
              {getPeriodLabel()}
            </span>
          </div>
        )}

        {/* Stats Cards */}
        {statsLoading ? (
          <div className="stats-grid">
            {[1, 2, 3].map((i) => (
              <div className="stat-card" key={i} style={{ opacity: 0.5 }}>
                <h3>Carregando...</h3>
                <p className="stat-value">--</p>
              </div>
            ))}
          </div>
        ) : stats ? (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <ClockIcon width={20} height={20} />
              </div>
              <h3>Total de Horas</h3>
              <p className="stat-value">{formatDecimalToHours(stats.totalHours)}</p>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <CheckCircledIcon width={20} height={20} />
              </div>
              <h3>Dias Trabalhados</h3>
              <p className="stat-value">{stats.totalDays}</p>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <ActivityLogIcon width={20} height={20} />
              </div>
              <h3>Média Horas/Dia</h3>
              <p className="stat-value">{formatDecimalToHours(stats.averageHoursPerDay)}</p>
            </div>
          </div>
        ) : null}

        {/* Dashboard Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`dashboard-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            <CalendarIcon width={16} height={16} />
            Calendário
          </button>
          <button
            className={`dashboard-tab-btn ${activeTab === 'table' ? 'active' : ''}`}
            onClick={() => setActiveTab('table')}
          >
            <TableIcon width={16} height={16} />
            Tabela Detalhada
          </button>
        </div>

        {/* Calendar View */}
        {activeTab === 'calendar' && (
          <div className="calendar-section">
            <div className="calendar-nav">
              <button
                className="calendar-nav-btn"
                onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
              >
                <ChevronLeftIcon width={18} height={18} />
              </button>
              <h2>{format(calendarMonth, "MMMM 'de' yyyy", { locale: ptBR })}</h2>
              <button
                className="calendar-nav-btn"
                onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
              >
                <ChevronRightIcon width={18} height={18} />
              </button>
            </div>

            <div className="calendar-weekdays">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
                <div key={day} className="calendar-weekday">
                  {day}
                </div>
              ))}
            </div>

            <div className="calendar-grid">
              {calendarDays.map((day, i) => {
                if (day.empty) {
                  return <div key={`empty-${i}`} className="calendar-day empty" />;
                }
                return (
                  <div
                    key={day.dateStr}
                    className={`calendar-day ${day.status} ${day.isToday ? 'today' : ''}`}
                    title={day.tooltip}
                  >
                    <span className="day-number">{day.dayNumber}</span>
                    {day.hours != null && day.hours > 0 && (
                      <span className="day-hours">{formatDecimalToHours(day.hours)}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="calendar-legend">
              <span className="legend-item">
                <span className="legend-dot present"></span>
                Presente
              </span>
              <span className="legend-item">
                <span className="legend-dot pending"></span>
                Pendente
              </span>
              <span className="legend-item">
                <span className="legend-dot absent"></span>
                Sem registro
              </span>
            </div>
          </div>
        )}

        {/* Table View */}
        {activeTab === 'table' && (
          <div className="section table-section">
            <h2>Histórico Detalhado</h2>
            {records.length === 0 ? (
              <div className="empty-state-container">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                <p className="empty-title">Nenhum registro encontrado</p>
                <p className="empty-subtitle">Não há registros no período selecionado</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Função</th>
                        <th>Entrada</th>
                        <th>Saída</th>
                        <th>Total Horas</th>
                        <th>Status</th>
                        <th>Observação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRecords.map((record) => (
                        <tr key={record.id}>
                          <td className="col-time">
                            {(() => {
                              try {
                                return format(parseISO(record.date), 'dd/MM/yyyy');
                              } catch {
                                return record.date?.split('-').reverse().join('/') || '-';
                              }
                            })()}
                          </td>
                          <td>{record.position?.name || '-'}</td>
                          <td className="col-time">{record.entryTime || '-'}</td>
                          <td className="col-time">
                            {record.exitTime || '-'}
                            {/* Passou do teto do ciclo: a saída acima é a pagável. */}
                            {record.saidaReal && (
                              <span
                                className="ud-retido"
                                title={`Hora retida acima do teto do ciclo (${formatDecimalToHours(record.horasExcedentes)}). Sua saída batida foi ${hhmm(record.saidaReal)}.`}
                              >
                                real {hhmm(record.saidaReal)} · retido{' '}
                                {formatDecimalToHours(record.horasExcedentes)}
                              </span>
                            )}
                          </td>
                          <td className="cell-primary col-time">
                            {formatDecimalToHours(record.totalHours)}
                          </td>
                          <td>
                            {record.exitTime ? (
                              <span className="badge-success">
                                <CheckCircledIcon width={12} height={12} />
                                Completo
                              </span>
                            ) : (
                              <span className="badge-warning">
                                <ClockIcon width={12} height={12} />
                                Pendente
                              </span>
                            )}
                          </td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.observation || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  id="user-records"
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  total={pagination.total}
                  from={pagination.from}
                  to={pagination.to}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={[15, 30, 60, 120]}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                  itemLabel="registros"
                />
              </>
            )}
          </div>
        )}

        {/* Period Summary (Standard 16-15) */}
        {periodSummary && (
          <div className="period-summary">
            <h2>
              <ActivityLogIcon width={18} height={18} />
              Resumo do Período Padrão ({getStandardPeriodLabel()})
            </h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total de Horas</h3>
                <p className="stat-value">{formatDecimalToHours(periodSummary.totalHours)}</p>
              </div>
              <div className="stat-card">
                <h3>Dias no Período</h3>
                <p className="stat-value">{periodSummary.totalDays}</p>
              </div>
              <div className="stat-card">
                <h3>Média Horas/Dia</h3>
                <p className="stat-value">{formatDecimalToHours(periodSummary.averageHoursPerDay)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Alterar Senha */}
      {showPasswordModal && (
        <div className="pwd-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="pwd-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pwd-modal-close" onClick={() => setShowPasswordModal(false)}>
              &times;
            </button>
            <div className="pwd-modal-icon-wrapper">
              <div className="pwd-modal-icon">
                <LockClosedIcon width={28} height={28} />
              </div>
            </div>
            <h2 className="pwd-modal-title">Alterar Senha</h2>
            <p className="pwd-modal-subtitle">
              Sua senha padrão é {userDetails?.personnelType === 'civil' ? 'o CPF' : 'a matrícula'}. Recomendamos alterar no primeiro acesso.
            </p>
            <form onSubmit={handleChangePassword} className="pwd-modal-form">
              <div className="pwd-field">
                <label className="pwd-label">Senha Atual</label>
                <div className="pwd-input-wrapper">
                  <LockClosedIcon width={16} height={16} className="pwd-input-icon" />
                  <input
                    type="password"
                    className="pwd-input"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Digite sua senha atual"
                    required
                  />
                </div>
              </div>
              <div className="pwd-field">
                <label className="pwd-label">Nova Senha</label>
                <div className="pwd-input-wrapper">
                  <LockClosedIcon width={16} height={16} className="pwd-input-icon" />
                  <input
                    type="password"
                    className="pwd-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                  />
                </div>
                {newPassword.length > 0 && (
                  <div className="pwd-strength">
                    <div className={`pwd-strength-bar ${newPassword.length >= 10 ? 'strong' : newPassword.length >= 6 ? 'medium' : 'weak'}`}></div>
                    <span className="pwd-strength-text">
                      {newPassword.length >= 10 ? 'Forte' : newPassword.length >= 6 ? 'Média' : 'Fraca'}
                    </span>
                  </div>
                )}
              </div>
              <div className="pwd-field">
                <label className="pwd-label">Confirmar Nova Senha</label>
                <div className="pwd-input-wrapper">
                  <CheckCircledIcon width={16} height={16} className="pwd-input-icon" />
                  <input
                    type="password"
                    className="pwd-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a nova senha"
                    required
                  />
                </div>
                {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                  <span className="pwd-mismatch">As senhas não coincidem</span>
                )}
              </div>
              <div className="pwd-modal-actions">
                <button type="button" className="pwd-btn-cancel" onClick={() => setShowPasswordModal(false)}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="pwd-btn-submit"
                  disabled={changingPassword || (confirmPassword.length > 0 && newPassword !== confirmPassword)}
                >
                  {changingPassword ? (
                    <>
                      <div className="pwd-btn-spinner"></div>
                      Alterando...
                    </>
                  ) : (
                    'Alterar Senha'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Aviso de distância: nunca bloqueia — o militar decide registrar mesmo assim */}
      {geoAviso && (
        <div className="pwd-modal-overlay" onClick={() => !punching && setGeoAviso(null)}>
          <div className="pwd-modal geo-aviso-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="pwd-modal-close"
              onClick={() => setGeoAviso(null)}
              disabled={punching}
            >
              &times;
            </button>
            <div className="pwd-modal-icon-wrapper">
              <div className="pwd-modal-icon warn">
                <ExclamationTriangleIcon width={28} height={28} />
              </div>
            </div>
            <h2 className="pwd-modal-title">Você está longe do colégio</h2>
            <p className="pwd-modal-subtitle">
              Sua localização está a{' '}
              <strong className="geo-aviso-dist">{formatDistance(geoAviso.distanceMeters)}</strong>{' '}
              do colégio
              {geoAviso.pos.accuracy != null &&
                ` (precisão ±${Math.round(geoAviso.pos.accuracy)} m)`}
              . O raio esperado é de {formatDistance(geoAviso.radiusMeters)}.
            </p>
            <p className="pwd-modal-subtitle">
              Registrar a {geoAviso.tipo === 'entrada' ? 'entrada' : 'saída'} mesmo assim? O
              registro fica marcado como <strong>fora do raio</strong>, com a distância gravada,
              e a administração e o fiscal veem isso nas listas.
            </p>
            <div className="pwd-modal-actions">
              <button
                type="button"
                className="pwd-btn-cancel"
                onClick={() => setGeoAviso(null)}
                disabled={punching}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pwd-btn-submit warn"
                disabled={punching}
                onClick={() => enviarPonto(geoAviso.tipo, geoAviso.pos)}
              >
                {punching ? (
                  <>
                    <div className="pwd-btn-spinner"></div>
                    Registrando…
                  </>
                ) : (
                  'Registrar mesmo assim'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserDashboard;
